import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { getRouteApi, Link } from '@tanstack/react-router';
import { COLLECTOR_ID_TYPES, type CollectorIdType } from '@wodi/domain';
import type { MortuaryCaseView, ReleaseRequestView } from '@wodi/shared';
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  LoadingBlock,
  Notice,
  PageHeader,
  Section,
  Select,
  Tag,
  TextArea,
  TextField,
} from '@wodi/ui';
import { useState } from 'react';

import {
  cancelMortuaryCase,
  decideRelease,
  moveBody,
  mortuaryBoardQuery,
  mortuaryCaseQuery,
  requestRelease,
} from '../api/mortuary.ts';
import { can, sessionQuery } from '../api/session.ts';
import { money } from '../billing/format.ts';
import { useOnline } from '../hooks/use-online.ts';
import { PatientBannerLoader } from '../patients/patient-banner.tsx';
import { formatInstant, formatIsoDate } from '../patients/format.ts';
import { ReasonDialog } from '../patients/reason-dialog.tsx';
import { toasts } from '../toasts.ts';
import styles from '../vital-events/vital-events.module.css';
import {
  CASE_STATUS_TAGS,
  errorText,
  ID_TYPE_WORDS,
  PLACE_WORDS,
  REQUEST_TAGS,
} from '../vital-events/words.ts';

// One body in the mortuary (docs/plans/phase2-mortuary-and-notifications.md §6): where it is,
// the storage days charged, the balance owed, and release to the person collecting it: the
// attendant records who and checks their identity document; a second person approves.

const route = getRouteApi('/_app/mortuary/cases/$caseId');

function useCaseUpdate(caseId: string) {
  const queryClient = useQueryClient();
  return (view: MortuaryCaseView) => {
    queryClient.setQueryData(mortuaryCaseQuery(caseId).queryKey, view);
    void queryClient.invalidateQueries({ queryKey: mortuaryBoardQuery.queryKey });
  };
}

function MoveDialog({ view, onClose }: { view: MortuaryCaseView; onClose: () => void }) {
  const board = useQuery(mortuaryBoardQuery);
  const update = useCaseUpdate(view.id);
  const [slotId, setSlotId] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const free = (board.data?.coldRooms ?? []).flatMap((r) =>
    r.slots
      .filter((s) => s.state === 'free')
      .map((s) => ({ id: s.id, label: `${r.name}, ${s.label}` })),
  );
  const move = useMutation({
    mutationFn: () =>
      moveBody(view.id, {
        slotId: slotId ?? '',
        ...(view.slot === null ? {} : { reason: reason.trim() }),
      }),
    onSuccess: (next) => {
      update(next);
      toasts.show({
        title: 'Body moved',
        description: next.slot === null ? '' : `${next.slot.coldRoom}, ${next.slot.label}`,
      });
      onClose();
    },
  });
  const ready = slotId !== null && (view.slot === null || reason.trim().length >= 5);
  return (
    <Dialog
      title={view.slot === null ? 'Put in a slot' : 'Move to another slot'}
      isOpen
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <Select
        label="Slot"
        placeholder={free.length === 0 ? 'No free slots' : 'Choose one'}
        options={free}
        value={slotId}
        onChange={(v) => {
          setSlotId(typeof v === 'string' ? v : null);
        }}
      />
      {view.slot === null ? null : (
        <TextArea label="Reason" value={reason} onChange={setReason} rows={2} />
      )}
      {move.error === null ? null : (
        <Notice tone="error">{errorText(move.error, 'The body was not moved.')}</Notice>
      )}
      <DialogActions>
        <Button variant="quiet" onPress={onClose}>
          Keep it here
        </Button>
        <Button
          variant="primary"
          isDisabled={!ready}
          isPending={move.isPending}
          pendingLabel="Moving…"
          onPress={() => {
            move.mutate();
          }}
        >
          {view.slot === null ? 'Put in slot' : 'Move body'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function RequestFacts({ request }: { request: ReleaseRequestView }) {
  return (
    <dl className={styles.facts}>
      <dt>Collector</dt>
      <dd>{request.collectorName}</dd>
      <dt>Relationship</dt>
      <dd>{request.collectorRelationship}</dd>
      <dt>Identity document</dt>
      <dd>
        {`${ID_TYPE_WORDS[request.collectorIdType]} `}
        <span className="wodi-id">{request.collectorIdNumber}</span>
        {request.idChecked ? ', seen by the attendant' : ''}
      </dd>
      {request.collectorPhone === null ? null : (
        <>
          <dt>Phone</dt>
          <dd>{request.collectorPhone}</dd>
        </>
      )}
      {request.documentsNote === null ? null : (
        <>
          <dt>Documents seen</dt>
          <dd>{request.documentsNote}</dd>
        </>
      )}
      <dt>Balance when asked</dt>
      <dd className={styles.money}>{money(request.balance)}</dd>
      <dt>Asked by</dt>
      <dd>{`${request.requestedBy.displayName}, ${formatInstant(request.requestedAt)}`}</dd>
      {request.decidedBy === null ? null : (
        <>
          <dt>Decided by</dt>
          <dd>
            {`${request.decidedBy.displayName}, ${request.decidedAt === null ? '' : formatInstant(request.decidedAt)}`}
            {request.decisionNote === null ? '' : `. ${request.decisionNote}`}
          </dd>
        </>
      )}
    </dl>
  );
}

function RecordCollection({ view }: { view: MortuaryCaseView }) {
  const online = useOnline();
  const update = useCaseUpdate(view.id);
  const [clientRequestId, setClientRequestId] = useState(() => crypto.randomUUID());
  const [name, setName] = useState('');
  const [relationship, setRelationship] = useState('');
  const [idType, setIdType] = useState<CollectorIdType>('national_id');
  const [idNumber, setIdNumber] = useState('');
  const [phone, setPhone] = useState('');
  const [seen, setSeen] = useState(false);
  const [documents, setDocuments] = useState('');
  const ask = useMutation({
    mutationFn: () =>
      requestRelease(view.id, {
        clientRequestId,
        collectorName: name.trim(),
        collectorRelationship: relationship.trim(),
        collectorIdType: idType,
        collectorIdNumber: idNumber.trim(),
        collectorPhone: phone.trim() === '' ? null : phone.trim(),
        idChecked: true,
        documentsNote: documents.trim() === '' ? null : documents.trim(),
      }),
    onSuccess: (next) => {
      update(next);
      setClientRequestId(crypto.randomUUID());
      toasts.show({ title: 'Release requested', description: 'A second person approves it.' });
    },
  });
  const ready =
    online &&
    seen &&
    name.trim().length >= 2 &&
    relationship.trim().length >= 2 &&
    idNumber.trim().length >= 2 &&
    !ask.isPending;
  return (
    <form
      className={styles.form}
      aria-label="Record collection"
      onSubmit={(e) => {
        e.preventDefault();
        if (ready) ask.mutate();
      }}
    >
      <div className={styles.row}>
        <TextField label="Collector's name" value={name} onChange={setName} />
        <TextField
          label="Relationship to the deceased"
          value={relationship}
          onChange={setRelationship}
        />
      </div>
      <div className={styles.row}>
        <Select
          label="Identity document"
          options={COLLECTOR_ID_TYPES.map((t) => ({ id: t, label: ID_TYPE_WORDS[t] }))}
          value={idType}
          onChange={(v) => {
            if (typeof v === 'string') setIdType(v as CollectorIdType);
          }}
        />
        <TextField label="Document number" value={idNumber} onChange={setIdNumber} />
        <TextField
          label="Phone (optional)"
          description="Like 254712345678"
          value={phone}
          onChange={setPhone}
        />
      </div>
      <TextArea
        label="Documents seen (optional)"
        description="For example a burial permit number."
        value={documents}
        onChange={setDocuments}
        rows={2}
      />
      <Checkbox isSelected={seen} onChange={setSeen}>
        I have seen the collector&apos;s identity document
      </Checkbox>
      <p
        className={styles.meta}
      >{`Balance owed now: ${money(view.balance.total)}. Release is not held for payment; the balance is recorded.`}</p>
      {ask.error === null ? null : (
        <Notice tone="error">{errorText(ask.error, 'The release was not requested.')}</Notice>
      )}
      <div className={styles.actions}>
        <Button
          type="submit"
          variant="primary"
          isDisabled={!ready}
          isPending={ask.isPending}
          pendingLabel="Asking…"
        >
          Ask to release
        </Button>
      </div>
    </form>
  );
}

function Release({ view }: { view: MortuaryCaseView }) {
  const { data: session } = useSuspenseQuery(sessionQuery);
  const online = useOnline();
  const update = useCaseUpdate(view.id);
  const [rejecting, setRejecting] = useState(false);
  const decide = useMutation({
    mutationFn: (input: { id: string; action: 'approve' | 'reject' | 'withdraw'; note?: string }) =>
      decideRelease(input.id, input.action, input.note),
    onSuccess: (next, input) => {
      update(next);
      setRejecting(false);
      toasts.show({
        title:
          input.action === 'approve'
            ? 'Body released'
            : input.action === 'reject'
              ? 'Release rejected'
              : 'Release withdrawn',
      });
    },
  });
  const open = view.releaseRequests.find((r) => r.status === 'requested');
  const past = view.releaseRequests.filter((r) => r.status !== 'requested');
  const me = session?.user.id;
  const mayApprove = session !== null && can(session, 'mortuary.release.approve');
  const mayAsk = session !== null && can(session, 'mortuary.release.request');
  return (
    <Section title="Release">
      {view.status === 'released' ? (
        <p>
          {`Released ${view.releasedAt === null ? '' : formatInstant(view.releasedAt)}. Balance at release: `}
          <span className={styles.money}>
            {view.releaseBalance === null ? '' : money(view.releaseBalance)}
          </span>
        </p>
      ) : null}
      {open === undefined ? null : (
        <>
          <p className={styles.actions}>
            <Tag state={REQUEST_TAGS.requested[1]}>{REQUEST_TAGS.requested[0]}</Tag>
          </p>
          <RequestFacts request={open} />
          {decide.error === null ? null : (
            <Notice tone="error">{errorText(decide.error, 'That did not work. Try again.')}</Notice>
          )}
          <div className={styles.actions}>
            {mayApprove && open.requestedBy.id !== me ? (
              <>
                <Button
                  variant="primary"
                  isDisabled={!online}
                  isPending={decide.isPending}
                  pendingLabel="Releasing…"
                  onPress={() => {
                    decide.mutate({ id: open.id, action: 'approve' });
                  }}
                >
                  Approve release
                </Button>
                <Button
                  isDisabled={!online}
                  onPress={() => {
                    setRejecting(true);
                  }}
                >
                  Reject
                </Button>
              </>
            ) : null}
            {open.requestedBy.id === me ? (
              <Button
                variant="quiet"
                isDisabled={!online}
                onPress={() => {
                  decide.mutate({ id: open.id, action: 'withdraw' });
                }}
              >
                Withdraw
              </Button>
            ) : null}
            {mayApprove && open.requestedBy.id === me ? (
              <p className={styles.meta}>
                You asked for this release, so someone else approves it.
              </p>
            ) : null}
          </div>
        </>
      )}
      {view.status === 'in_storage' && open === undefined ? (
        mayAsk ? (
          <RecordCollection view={view} />
        ) : (
          <p className={styles.meta}>The mortuary attendant records who collects the body.</p>
        )
      ) : null}
      {past.length === 0 ? null : (
        <ul className={styles.list} aria-label="Earlier release requests">
          {past.map((r) => (
            <li key={r.id} className={styles.listRow}>
              <span>{`${r.collectorName}, asked ${formatInstant(r.requestedAt)}${r.decisionNote === null ? '' : `. ${r.decisionNote}`}`}</span>
              <Tag state={REQUEST_TAGS[r.status][1]}>{REQUEST_TAGS[r.status][0]}</Tag>
            </li>
          ))}
        </ul>
      )}
      <ReasonDialog
        title="Reject the release"
        isOpen={rejecting}
        onOpenChange={setRejecting}
        confirmLabel="Reject"
        pendingLabel="Rejecting…"
        isPending={decide.isPending}
        error={null}
        onConfirm={(note) => {
          if (open !== undefined) decide.mutate({ id: open.id, action: 'reject', note });
        }}
      />
    </Section>
  );
}

export function MortuaryCasePage() {
  const { caseId } = route.useParams();
  const { data: session } = useSuspenseQuery(sessionQuery);
  const found = useQuery(mortuaryCaseQuery(caseId));
  const online = useOnline();
  const update = useCaseUpdate(caseId);
  const [dialog, setDialog] = useState<'move' | 'cancel' | null>(null);
  const cancel = useMutation({
    mutationFn: (reason: string) => cancelMortuaryCase(caseId, reason),
    onSuccess: (next) => {
      update(next);
      setDialog(null);
      toasts.show({ title: 'Mortuary case cancelled' });
    },
  });
  if (found.isPending) return <LoadingBlock label="Loading the mortuary case" rows={6} />;
  if (found.isError) {
    return (
      <Notice tone="error" action={<Button onPress={() => void found.refetch()}>Try again</Button>}>
        {errorText(found.error, "The mortuary case couldn't be loaded. Try again.")}
      </Notice>
    );
  }
  const c = found.data;
  const [word, shape] = CASE_STATUS_TAGS[c.status];
  const inStorage = c.status === 'in_storage';
  const mayMove = session !== null && can(session, 'mortuary.intake');
  const mayCancel = session !== null && can(session, 'mortuary.case.cancel');
  const mayReadDeaths = session !== null && can(session, 'deaths.read');
  return (
    <div className={styles.page}>
      <PatientBannerLoader patientId={c.patientId} />
      <PageHeader
        title={`Mortuary case ${c.caseNumber}`}
        description={`Received ${formatInstant(c.receivedAt)} by ${c.receivedBy.displayName}. Day ${String(c.daysInStorage)}.`}
        actions={
          inStorage ? (
            <>
              {mayMove ? (
                <Button
                  isDisabled={!online}
                  onPress={() => {
                    setDialog('move');
                  }}
                >
                  {c.slot === null ? 'Put in a slot' : 'Move to another slot'}
                </Button>
              ) : null}
              {mayCancel ? (
                <Button
                  variant="quiet"
                  isDisabled={!online}
                  onPress={() => {
                    setDialog('cancel');
                  }}
                >
                  Cancel case
                </Button>
              ) : null}
            </>
          ) : undefined
        }
      />
      {c.cancelled === null ? null : (
        <Notice tone="info">
          {`Cancelled as entered in error on ${formatInstant(c.cancelled.at)} by ${c.cancelled.by.displayName}: ${c.cancelled.reason}`}
        </Notice>
      )}
      <Section title="Body" actions={<Tag state={shape}>{word}</Tag>}>
        <dl className={styles.facts}>
          <dt>Case number</dt>
          <dd className="wodi-id">{c.caseNumber}</dd>
          <dt>Where now</dt>
          <dd>{c.slot === null ? 'Not in a slot' : `${c.slot.coldRoom}, slot ${c.slot.label}`}</dd>
          <dt>Death</dt>
          <dd>
            {`${PLACE_WORDS[c.death.place]}, ${formatInstant(c.death.diedAt)} `}
            {mayReadDeaths ? (
              <Link to="/deaths/$deathId" params={{ deathId: c.death.id }}>
                Open the death record
              </Link>
            ) : null}
          </dd>
          {c.broughtBy === null ? null : (
            <>
              <dt>Brought by</dt>
              <dd>{c.broughtBy}</dd>
            </>
          )}
          {c.intakeNotes === null ? null : (
            <>
              <dt>Intake notes</dt>
              <dd className={styles.prose}>{c.intakeNotes}</dd>
            </>
          )}
        </dl>
        {c.slotHistory.length > 1 ? (
          <ul className={styles.list} aria-label="Slot history">
            {c.slotHistory.map((h) => (
              <li key={h.id} className={styles.listRow}>
                <span>{`${h.coldRoom}, ${h.label}`}</span>
                <span className={styles.meta}>
                  {`${formatInstant(h.startedAt)}${h.endedAt === null ? ', now' : ` to ${formatInstant(h.endedAt)}`}${h.reason === null ? '' : `. ${h.reason}`}`}
                </span>
              </li>
            ))}
          </ul>
        ) : null}
      </Section>
      <Section title="Storage and balance">
        <dl className={styles.facts}>
          <dt>Storage days charged</dt>
          <dd>
            {c.storageDays.length === 0
              ? 'None yet: the first day is charged at the first midnight, or at release.'
              : c.storageDays
                  .map(
                    (d) =>
                      `${formatIsoDate(d.serviceDate)}${d.chargeStatus === 'billed' ? ' (billed)' : d.priced ? '' : ' (no price)'}`,
                  )
                  .join(', ')}
          </dd>
          <dt>Owed on the mortuary bill</dt>
          <dd className={styles.money}>{money(c.balance.mortuary)}</dd>
          {c.balance.deathVisit === null ? null : (
            <>
              <dt>Owed on the visit of death</dt>
              <dd className={styles.money}>{money(c.balance.deathVisit)}</dd>
            </>
          )}
          <dt>Total owed</dt>
          <dd className={styles.money}>{money(c.balance.total)}</dd>
        </dl>
      </Section>
      {c.status === 'entered_in_error' ? null : <Release view={c} />}
      {dialog === 'move' ? (
        <MoveDialog
          view={c}
          onClose={() => {
            setDialog(null);
          }}
        />
      ) : null}
      <ReasonDialog
        title="Cancel mortuary case"
        isOpen={dialog === 'cancel'}
        onOpenChange={(o) => {
          if (!o) setDialog(null);
        }}
        confirmLabel="Cancel case"
        pendingLabel="Cancelling…"
        isPending={cancel.isPending}
        error={
          cancel.error === null ? null : errorText(cancel.error, 'The case was not cancelled.')
        }
        onConfirm={(reason) => {
          cancel.mutate(reason);
        }}
      >
        <p className={styles.meta}>
          Only for a case received in error. Storage charges not yet billed are cancelled.
        </p>
      </ReasonDialog>
    </div>
  );
}
